import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { ARCGIS_BASEMAP_CACHES } from 'utils/serviceWorkerMapCache/cacheNames';

export const semverCompare = (versionA: string, versionB: string): -1 | 0 | 1 => {
  const versionsA = versionA.split('.').map(Number);
  const versionsB = versionB.split('.').map(Number);

  const maxLength = Math.max(versionsA.length, versionsB.length);

  for (let i = 0; i < maxLength; i++) {
    const a = versionsA[i] || 0;
    const b = versionsB[i] || 0;

    if (a < b) return -1;
    if (a > b) return 1;
  }

  return 0;
};

// Public basemap imagery, not tenant data — clearing it on every release would cost users their
// offline map on each deploy.
const PRESERVED_CACHES: readonly string[] = ARCGIS_BASEMAP_CACHES;

export const clearCache = async (): Promise<void> => {
  if (!navigator.onLine) {
    loggerService.log(LoggerLevel.DEBUG, 'No internet connection, skipping cache busting');
    return;
  }

  try {
    loggerService.log(LoggerLevel.DEBUG, 'Starting cache busting...');

    // 1. Unregister service worker to clear SW caches and force fresh registration
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.unregister();
          loggerService.log(LoggerLevel.DEBUG, 'Unregistered service worker');
        } else {
          loggerService.log(LoggerLevel.DEBUG, 'No service worker registered');
        }
      } catch (error) {
        loggerService.log(LoggerLevel.DEBUG, 'No service worker to unregister', error);
      }
    }

    // 2. Clear service worker caches, except the basemap ones
    if (caches) {
      const names = await caches.keys();
      for (const name of names) {
        if (!PRESERVED_CACHES.includes(name)) {
          await caches.delete(name);
        }
      }
      loggerService.log(
        LoggerLevel.DEBUG,
        'Cleared browser caches, preserving basemap caches',
        PRESERVED_CACHES
      );
    }

    // 3. Clear React Query cache and persisted storage
    const { clearQueryClientCache } = await import(
      'common/infrastructure/remoteState/persistQueryClient'
    );
    await clearQueryClientCache();
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Cache busting failed', error);
  }
};
